/**
 * *************************************
 *  SHADAPPS CONFIDENTIAL
 *  __________________
 *
 *  Created by Vittorio Tauro
 *
 *  2022 (c) ShadApps Srl
 *  All Rights Reserved.
 *
 *  NOTICE:  All information contained herein is, and remains
 *  the property of ShadApps Srl and its suppliers,
 *  if any. The intellectual and technical concepts contained
 *  herein are proprietary to ShadApps Srl.
 *  and its suppliers and may be covered by Italian, European and Foreign Patents,
 *  patents in process, and are protected by trade secret or copyright law.
 *  Dissemination of this information or reproduction of this material
 *  is strictly forbidden unless prior written permission is obtained
 *  from ShadApps Srl.
 * *************************************
 * */

import { lazy, Suspense } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// auth
import AuthGuard from '../auth/AuthGuard';
import GuestGuard from '../auth/GuestGuard';
// components
import { LoadingScreen } from '../components/loading-screen';
// hooks
import { useAuthContext as useAuth } from '../auth/useAuthContext';
import { ModalContextProvider } from '../layouts/datatable/DatatableModalStore';
// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { pathname } = useLocation();

    return (
        <Suspense
            fallback={
                <LoadingScreen isDashboard={pathname.includes('/dashboard')} />
            }
        >
            <Component {...props} />
        </Suspense>
    );
};

export default function Router() {
    const { user } = useAuth();

    return useRoutes([
        {
            path: '/',
            children: [
                {
                    path: 'login',
                    element: (
                        <GuestGuard>
                            <Login />
                        </GuestGuard>
                    ),
                },
                {
                    path: 'reset-password',
                    element: (
                        <GuestGuard>
                            <ResetPassword />
                        </GuestGuard>
                    ),
                },
            ],
        },
        {
            path: '/',
            element: (
                <AuthGuard>
                    <ModalContextProvider>
                        <DashboardLayout />
                    </ModalContextProvider>
                </AuthGuard>
            ),
            children: [
                { element: <Navigate to="/home" replace />, index: true },
                { path: 'home', element: <Home /> },
                {
                    path: 'trips/:crudId',
                    element: user ? <TripByFlight /> : <Page403 />,
                },
                { path: ':urlEntity', element: user ? <Crud /> : <Page403 /> },
                // { path: 'bookings/:crudId', element: user ? 'hey' : <Page403/> },
            ],
        },
        {
            path: '*',
            element: <LogoOnlyLayout />,
            children: [
                { path: '404', element: <NotFound /> },
                { path: '*', element: <Navigate to="/404" replace /> },
                { path: '500', element: <Page500 /> },
                { path: '404', element: <Page404 /> },
                { path: '403', element: <Page403 /> },
                { path: 'logout', element: <LogoutPage /> },
            ],
        },
        { path: '*', element: <Navigate to="/404" replace /> },
    ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));

// PAGES
const Home = Loadable(lazy(() => import('../pages/custom/Home')));
const Users = Loadable(lazy(() => import('../pages/crud/UsersPage')));
const Crud = Loadable(lazy(() => import('../pages/crud/CrudPage')));
const TripByFlight = Loadable(
    lazy(() => import('../pages/crud/TripsByFlightPage'))
);
const NotFound = Loadable(lazy(() => import('../pages/Page404')));

// MAIN
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
const LogoutPage = Loadable(lazy(() => import('../pages/LogoutPage')));
